import React from 'react';
import { Container, Typography, Box } from '@mui/material';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const values = [
  {
    title: 'People-Centered',
    description: 'Creating intuitive digital solutions that put users first and make technology accessible for everyone.'
  },
  {
    title: 'Tech-Focused',
    description: 'Building scalable applications with cutting-edge technologies that drive measurable business growth.'
  },
  {
    title: 'Creativity-Led',
    description: 'Transforming complex challenges into elegant solutions with innovative design thinking.'
  },
  {
    title: 'Data-Driven',
    description: 'Optimizing performance through analytics and real-world insights for maximum impact.'
  }
];

const ValuesSection = () => {
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true
  });

  return (
    <Box
      ref={ref}
      sx={{
        py: { xs: 10, md: 15 },
        background: '#fff',
        position: 'relative'
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: { xs: 8, md: 10 } }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2.5rem', sm: '3rem', md: '3rem' },
                fontWeight: 800,
                color: '#111',
                mb: 2,
                '& span': {
                  color: '#3F72AF'
                }
              }}
            >
              Our Core <span>Values</span>
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: '1.1rem', md: '1.25rem' },
                color: '#666',
                maxWidth: '800px',
                margin: '0 auto',
                lineHeight: 1.8
              }}
            >
              At Virves, we build exceptional digital solutions through a culture of innovation, technical excellence, and user-centered design. Every line of code we write serves a purpose.
            </Typography>
          </motion.div>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' },
            gap: { xs: 4, md: 3 },
            mt: 8
          }}
        >
          {values.map((value, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <Box
                sx={{
                  background: '#fff',
                  borderRadius: '20px',
                  p: 4,
                  height: '100%',
                  textAlign: 'center',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.08)',
                  }
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { xs: '1.5rem', md: '1.75rem' },
                    fontWeight: 700,
                    color: '#111',
                    mb: 2,
                    position: 'relative',
                    display: 'inline-block',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: '-8px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '40px',
                      height: '2px',
                      backgroundColor: '#3F72AF',
                      opacity: 0.7
                    }
                  }}
                >
                  {value.title}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '1rem',
                    color: '#666',
                    lineHeight: 1.7,
                    mt: 3
                  }}
                >
                  {value.description}
                </Typography>
              </Box>
            </motion.div>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default ValuesSection; 